import React, { useState } from 'react'
import classNames from 'classnames'

import styles from '../component-styles/campaign-codes-administration.module.scss'
import { FormRow, LabelSlot, FieldSlot } from './row'
import Input, { Label, ClickEditInput } from './input'
import ToolTip from './tooltip'
import AdobeFTPDetails from './adobe-ftp-details'
import {
  CampaignCodeGeneratorStructure,
  ValidationChecks,
} from '../api-client/api-types'
import ManageCampaignCodes from './manage-campaign-codes'
import { getValidationCheck } from '../helpers/index'

const updateValidationChecks = (
  validationChecks,
  key,
  enabled,
  value: any = undefined,
) => {
  const found = validationChecks.some((item) => item.name === key)
  if (!found) {
    const obj =
      typeof value !== 'undefined'
        ? { enabled, name: key, value }
        : { enabled, name: key }
    return [...validationChecks, obj]
  }
  return validationChecks.concat().map((item) => {
    const { name } = item
    if (name === key) {
      const res =
        typeof value !== 'undefined'
          ? { enabled, name, value }
          : { enabled, name }
      return res
    }
    return item
  })
}

// const getValidationCheck = (validationChecks, key, defaultValue = false) => {
//   const found = validationChecks.find((item) => item.name === key)
//   if (!found) {
//     if (key === 'REPLACE_SPACES_WITH') {
//       return {
//         enabled: true,
//         name: key,
//         value: '_',
//       }
//     }
//     if (key === 'LIMIT_URL_LENGTH') {
//       return {
//         enabled: true,
//         name: key,
//         value: 1024,
//       }
//     }
//     if (key === 'COPY_FROM_SEPARATOR') {
//       return {
//         enabled: true,
//         name: key,
//         value: '|',
//       }
//     }
//     return {
//       enabled: defaultValue,
//       name: key,
//     }
//   }
//   return found
// }

interface Props {
  masterPrefix: string
  paramSeparator: string
  mobile?: boolean
  validationChecks: ValidationChecks[]
  form: CampaignCodeGeneratorStructure
  onRemove: (fieldID: string) => void
  onChangeOrder: (changes: {
    masterPrefix?: string
    validationChecks?: ValidationChecks[]
    paramSeparator?: string
    paramDefs: any[]
  }) => void
  onChange: (changes: {
    masterPrefix?: string
    validationChecks?: ValidationChecks[]
    paramSeparator?: string
  }) => void
}

export default function CampaignCodesAdministrationMasterAndSelector({
  masterPrefix,
  paramSeparator,
  validationChecks,
  onChange,
  onRemove,
  onChangeOrder,
  mobile,
  form,
}: Props): React.ReactElement {
  const [rl, setRl] = useState(
    getValidationCheck(validationChecks, 'REQUIRE_LANDING_PAGE').enabled,
  )
  const [sl, setSl] = useState(
    getValidationCheck(validationChecks, 'SHOW_LANDING_PAGE').enabled,
  )
  const [lc, setLc] = useState(
    getValidationCheck(validationChecks, 'ALL_LOWER_CASE').enabled,
  )
  const [ns, setNs] = useState(
    getValidationCheck(validationChecks, 'NO_SPECIAL_CHARS').enabled,
  )
  const [np, setNp] = useState(
    getValidationCheck(validationChecks, 'NO_SPACES').enabled,
  )

  const spacesWith = getValidationCheck(validationChecks, 'REPLACE_SPACES_WITH')
  const [spaces, setSpaces] = useState(spacesWith.enabled)
  const [spacesValue, setSpacesValue] = useState(spacesWith.value)

  const limitURL = getValidationCheck(validationChecks, 'LIMIT_URL_LENGTH')
  const [urlLength, setUrlLength] = useState(limitURL.enabled)
  const [urlLengthValue, setUrlLengthValue] = useState(limitURL.value)

  const limitQuery = getValidationCheck(validationChecks, 'LIMIT_QUERY_LENGTH')
  const [queryLength, setQueryLength] = useState(limitQuery.enabled)
  const [queryLengthValue, setQueryLengthValue] = useState(limitQuery.value)

  const [copyFromSeparatorValue, setCopyFromSeparatorValue] = useState(
    getValidationCheck(validationChecks, 'COPY_FROM_SEPARATOR').value,
  )

  const [ip, setIp] = useState(
    getValidationCheck(validationChecks, 'INCLUDE_PREFIX_WITH_COPY_FROM')
      .enabled,
  )

  const [fsl, setFsl] = useState(
    getValidationCheck(validationChecks, 'FORCE_SHORT_LINK', true).enabled,
  )

  const [ep, setEp] = useState(
    getValidationCheck(validationChecks, 'INCLUDE_EMPTY_VALUES').enabled,
  )
  const [nslp, setNsLP] = useState(
    getValidationCheck(validationChecks, 'NO_SPECIAL_CHARS_LANDING_PAGE')
      .enabled,
  )

  const [urlEx, setUrlEx] = useState(
    getValidationCheck(validationChecks, 'CHECK_URL_EXISTS', true).enabled,
  )
  const [urlSp, setUrlSp] = useState(
    getValidationCheck(validationChecks, 'CHECK_URL_SPEED', true).enabled,
  )
  const [urlRed, setUrlRed] = useState(
    getValidationCheck(validationChecks, 'CHECK_URL_REDIRECT', true).enabled,
  )
  const [urlAn, setUrlAn] = useState(
    getValidationCheck(validationChecks, 'CHECK_URL_ANALYTICS', true).enabled,
  )

  return (
    <>
      <h3>Prefix and separator</h3>
      <FormRow last>
        <LabelSlot>
          <Label id="master-prefix" className={styles.bold}>
            Prefix
          </Label>
          <ToolTip
            showUnder
            showOnRight
            message="The character(s) that immediately follow the landing page URL. This separates the landing page URL from the query string parameters and normally starts with a question mark."
          />
        </LabelSlot>
        <FieldSlot>
          <ClickEditInput
            showBorder
            id="master-prefix"
            name="master-prefix"
            value={masterPrefix}
            onChange={(value: string): void => {
              onChange({
                masterPrefix: value,
              })
            }}
          />
        </FieldSlot>
      </FormRow>
      <FormRow last>
        <LabelSlot>
          <Label id="master-separator" className={styles.bold}>
            Separator
          </Label>
          <ToolTip
            showOnRight
            message="Used to split up each query string parameter. This is usually an ampersand, but may be blank if combining multiple inputs into one query string parameter."
          />
        </LabelSlot>
        <FieldSlot>
          <ClickEditInput
            showBorder
            id="master-separator"
            name="master-separator"
            value={paramSeparator}
            onChange={(value: string): void => {
              onChange({
                paramSeparator: value,
              })
            }}
          />
        </FieldSlot>
      </FormRow>

      <div className={styles.checkboxPanel}>
        <h3>Landing page URL</h3>

        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="url-required"
            name="url-required"
            label="Required"
            checked={rl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setRl(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'REQUIRE_LANDING_PAGE',
                  checked,
                ),
              })
            }}
          >
            <ToolTip
              showOnRight
              message="If checked, users will have to enter a landing page URL to create a campaign code link."
            />
          </Input>
        </FieldSlot>
        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="url-show"
            name="url-show"
            label="Show this field"
            checked={sl}
            disabled={rl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setSl(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'SHOW_LANDING_PAGE',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If unchecked, the landing page URL input box will be removed (not recommended)." />
          </Input>
        </FieldSlot>

        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="no-specials"
            name="no-specials"
            label="No special characters (?=&amp;)"
            checked={nslp}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setNsLP(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'NO_SPECIAL_CHARS_LANDING_PAGE',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked, users will not be able to enter a URL with ?=&amp;. This stops them using a URL that might already contain a query string." />
          </Input>
        </FieldSlot>
      </div>

      <div className={styles.checkboxPanel}>
        <h3>Landing page validation</h3>
        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="check-url-exists"
            name="check-url-exists"
            label="Check it exists"
            checked={urlEx}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setUrlEx(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'CHECK_URL_EXISTS',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked the validation will check if the page exists." />
          </Input>
        </FieldSlot>
        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="check-url-speed"
            name="check-url-speed"
            label="Check it loads fast"
            checked={urlSp}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setUrlSp(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'CHECK_URL_SPEED',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked the validation will check if the page loads under 5 seconds." />
          </Input>
        </FieldSlot>
        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="check-url-redirect"
            name="check-url-redirect"
            label="Check it redirects"
            checked={urlRed}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setUrlRed(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'CHECK_URL_REDIRECT',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked the validation will check for page redirects." />
          </Input>
        </FieldSlot>
        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="check-url-analytics"
            name="check-url-analytics"
            label="Check it has analytics"
            checked={urlAn}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setUrlAn(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'CHECK_URL_ANALYTICS',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked the validation will check if the page has Google or Adobe analytics." />
          </Input>
        </FieldSlot>
      </div>

      <div className={styles.checkboxPanel}>
        <h3>Parameters</h3>
        <p>Add, edit, reorder and delete.</p>
        <ManageCampaignCodes
          form={form}
          onRemove={onRemove}
          onChange={onChange}
          onChangeOrder={onChangeOrder}
        />
      </div>

      <div className={styles.checkboxPanel}>
        <h3>Validation options</h3>

        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="lower-case"
            name="lower-case"
            label="Lower case only"
            checked={lc}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setLc(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'ALL_LOWER_CASE',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked, users' text input will be converted to lower case characters. This makes data easier to read in analytics platforms." />
          </Input>
        </FieldSlot>

        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="no-specials"
            name="no-specials"
            label="No special characters (?=&amp;)"
            checked={ns}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setNs(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'NO_SPECIAL_CHARS',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked, users' text input cannot contain special characters. This stops users creating campaign codes which interfere with campaign code tracking. " />
          </Input>
        </FieldSlot>

        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="no-spaces"
            name="no-spaces"
            label="No spaces"
            checked={np}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setNp(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'NO_SPACES',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked, users will not be allowed to enter spaces into free text parameters or dropdown codes. This stops ugly %20 characters from showing in your analytics tools." />
          </Input>
        </FieldSlot>
        <FieldSlot className={styles.fullWidth}>
          <Input
            className={styles.inputWithInput}
            type="checkbox"
            id="space-to-character-checkbox"
            name="space-to-character-checkbox"
            label="Replace spaces with"
            checked={spaces}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setSpaces(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'REPLACE_SPACES_WITH',
                  checked,
                  spacesValue,
                ),
              })
            }}
          >
            <Input
              className={styles.smallInput}
              id="space-to-character-value"
              name="space-to-character-value"
              placeholder="Empty"
              value={spacesValue}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>): void => {
                const { value } = e.target as HTMLInputElement
                if (value !== '' && !spaces) {
                  setSpaces(true)
                }
                setSpacesValue(value)

                onChange({
                  validationChecks: updateValidationChecks(
                    validationChecks,
                    'REPLACE_SPACES_WITH',
                    true,
                    value,
                  ),
                })
              }}
            >
              <ToolTip message="If checked, spaces will be replaced with this character when typing into free text parameters or dropdown codes. The default recommended value is underscore _." />
            </Input>
          </Input>
        </FieldSlot>
        <FieldSlot className={styles.fullWidth}>
          <Input
            className={styles.inputWithInput}
            type="checkbox"
            id="url-length-checkbox"
            name="url-length-checkbox"
            label="Max link length"
            checked={urlLength}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setUrlLength(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'LIMIT_URL_LENGTH',
                  checked,
                  urlLengthValue,
                ),
              })
            }}
          >
            <Input
              className={styles.smallInput}
              id="url-length-value"
              name="url-length-value"
              placeholder="Empty"
              value={urlLengthValue}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>): void => {
                const { value } = e.target as HTMLInputElement
                if (value !== '' && !urlLengthValue) {
                  setUrlLength(true)
                }
                setUrlLengthValue(value)

                onChange({
                  validationChecks: updateValidationChecks(
                    validationChecks,
                    'LIMIT_URL_LENGTH',
                    true,
                    value,
                  ),
                })
              }}
            >
              <span className={styles.chars}>characters</span>
              <ToolTip
                showOnLeft
                message="Maximum number of characters in the landing page and generated tracking code. Recommended value is 1024 characters."
              />
            </Input>
          </Input>
        </FieldSlot>
        <FieldSlot className={styles.fullWidth}>
          <Input
            className={styles.inputWithInput}
            type="checkbox"
            id="query-length-checkbox"
            name="query-length-checkbox"
            label="Max query length"
            checked={queryLength}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setQueryLength(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'LIMIT_QUERY_LENGTH',
                  checked,
                  queryLengthValue,
                ),
              })
            }}
          >
            <Input
              className={styles.smallInput}
              id="query-length-value"
              name="query-length-value"
              placeholder="Empty"
              value={queryLengthValue}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>): void => {
                const { value } = e.target as HTMLInputElement
                if (value !== '' && !queryLengthValue) {
                  setQueryLength(true)
                }
                setQueryLengthValue(value)

                onChange({
                  validationChecks: updateValidationChecks(
                    validationChecks,
                    'LIMIT_QUERY_LENGTH',
                    true,
                    value,
                  ),
                })
              }}
            >
              <span className={styles.chars}>characters</span>
              <ToolTip
                showOnLeft
                message="Maximum number of generated tracking code. Recommended value is 255 characters."
              />
            </Input>
          </Input>
        </FieldSlot>
      </div>

      <div className={styles.checkboxPanel}>
        <div className={styles.row}>
          <h3>Short link preferences</h3>
        </div>
        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="force-shortlink"
            name="force-shortlink"
            label="Force users to create short links"
            checked={fsl}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setFsl(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'FORCE_SHORT_LINK',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked, the only option for link type will be short link." />
          </Input>
        </FieldSlot>
      </div>
      <div className={styles.checkboxPanel}>
        <div className={styles.row}>
          <h3>Advanced options</h3>
        </div>
        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Input
            type="checkbox"
            id="always-include"
            name="always-include"
            label="Include prefix with 'copy from' fields"
            checked={ip}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setIp(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'INCLUDE_PREFIX_WITH_COPY_FROM',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked, any parameters copied from another parameter will include both the parameter prefix and the value." />
          </Input>
        </FieldSlot>
        <FieldSlot
          className={classNames(styles.fullWidth, styles.bottomPadding)}
        >
          <Label id="master-separator" className={styles.inlineLabel}>
            Copy from separator
          </Label>
          <Input
            className={styles.inlineInput}
            id="separator-value"
            name="separator-value"
            placeholder="(empty)"
            value={copyFromSeparatorValue}
            onBlur={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { value } = e.target as HTMLInputElement
              setCopyFromSeparatorValue(value)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'COPY_FROM_SEPARATOR',
                  true,
                  value,
                ),
              })
            }}
          >
            <ToolTip message="Used to split up each query string parameter from multiple values which are copied from existing fields where a prefix has been set." />
          </Input>
        </FieldSlot>

        <FieldSlot className={styles.fullWidth}>
          <Input
            type="checkbox"
            id="exclude-empty"
            name="exclude-empty"
            label="Include empty optional values"
            checked={ep}
            onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
              const { checked } = e.target as HTMLInputElement
              setEp(checked)
              onChange({
                validationChecks: updateValidationChecks(
                  validationChecks,
                  'INCLUDE_EMPTY_VALUES',
                  checked,
                ),
              })
            }}
          >
            <ToolTip message="If checked, empty input values will still be shown in the tool with their prefix." />
          </Input>
        </FieldSlot>
      </div>
      <AdobeFTPDetails />
    </>
  )
}
